import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, BriefcaseBusiness, ClipboardCheck, Users } from 'lucide-react';
import type { SalesPersonToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { formatDate } from '~/lib/format-date';
import { serverMessage } from '~/lib/api/server-error';
import { useSalesPersonsList, useSalesPersonMutations } from '~/hooks/use-sales-persons';
import { useLinkedInspectors, useToggleLinkedInspector, useLinkedCustomers, useToggleLinkedCustomer } from '~/hooks/use-sales-person-links';
import { SalesPersonEditDialog } from './sales-person-edit-dialog';
import { LinkToggleDialog } from './link-toggle-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Sales persons (Πωλητές) — full CRUD grid with per-row linked-inspectors and linked-customers sub-views. */
export function SalesPersonsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useSalesPersonsList();
	const { remove } = useSalesPersonMutations();
	const all = data ?? [];

	const [editRow, setEditRow] = useState<SalesPersonToken | null>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteRow, setDeleteRow] = useState<SalesPersonToken | null>(null);
	const [inspectorsFor, setInspectorsFor] = useState<SalesPersonToken | null>(null);
	const [customersFor, setCustomersFor] = useState<SalesPersonToken | null>(null);

	const linkedInspectors = useLinkedInspectors(inspectorsFor?.id ?? null);
	const toggleInspector = useToggleLinkedInspector(inspectorsFor?.id ?? null);
	const linkedCustomers = useLinkedCustomers(customersFor?.id ?? null);
	const toggleCustomer = useToggleLinkedCustomer(customersFor?.id ?? null);

	const nameById = useMemo(() => new Map(all.map(p => [p.id, [p.fn, p.ln].filter(Boolean).join(' ').trim() || p.usr])), [all]);
	const fld = (key: string) => t(`customers.salesPersons.fields.${key}`);

	const columns = useMemo<SystemGridColumn<SalesPersonToken>[]>(
		() => [
			{
				key: 'usr',
				header: fld('username'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.usr ?? '',
				exportValue: o => o.usr ?? '',
				render: o => <span className="truncate text-sm font-medium text-foreground">{o.usr || '—'}</span>
			},
			{
				key: 'fn',
				header: fld('name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.fn ?? '',
				exportValue: o => o.fn ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.fn || '—'}</span>
			},
			{
				key: 'ln',
				header: fld('surname'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.ln ?? '',
				exportValue: o => o.ln ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.ln || '—'}</span>
			},
			{
				key: 'act',
				header: fld('active'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.act ? 1 : 0),
				exportValue: o => (o.act ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.act)} />
			},
			{
				key: 'cfm',
				header: fld('confirmed'),
				width: '110px',
				align: 'center',
				defaultVisible: false,
				sortValue: o => (o.cfm ? 1 : 0),
				exportValue: o => (o.cfm ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.cfm)} />
			},
			{
				key: 'eml',
				header: fld('email'),
				width: 'minmax(0,1.2fr)',
				sortValue: o => o.eml ?? '',
				exportValue: o => o.eml ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.eml || '—'}</span>
			},
			{
				key: 'erpcd',
				header: fld('erpCode'),
				width: '120px',
				defaultVisible: false,
				sortValue: o => o.erpcd ?? '',
				exportValue: o => o.erpcd ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.erpcd || '—'}</span>
			},
			{
				key: 'rspid',
				header: fld('replaceSalesPerson'),
				width: 'minmax(0,1fr)',
				sortValue: o => (o.rspid != null ? (nameById.get(o.rspid) ?? '') : ''),
				exportValue: o => (o.rspid != null ? (nameById.get(o.rspid) ?? '') : ''),
				render: o => <span className="truncate text-sm text-foreground-muted">{(o.rspid != null && nameById.get(o.rspid)) || '—'}</span>
			},
			{
				key: 'rsd',
				header: fld('replaceStart'),
				width: '150px',
				defaultVisible: false,
				sortValue: o => (o.rsd as string | null) ?? '',
				exportValue: o => formatDate(o.rsd as string | null),
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{(o.rsd && formatDate(o.rsd as string)) || '—'}</span>
			},
			{
				key: 'red',
				header: fld('replaceEnd'),
				width: '150px',
				defaultVisible: false,
				sortValue: o => (o.red as string | null) ?? '',
				exportValue: o => formatDate(o.red as string | null),
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{(o.red && formatDate(o.red as string)) || '—'}</span>
			}
		],
		[t, nameById]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	const fullName = (p: SalesPersonToken) => [p.fn, p.ln].filter(Boolean).join(' ').trim() || p.usr || '';

	return (
		<>
			<SystemGrid<SalesPersonToken>
				gridId="sales-persons"
				title={t('customers.salesPersons.title')}
				rows={all}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.usr}
				columns={columns}
				searchPredicate={(o, q) =>
					(o.usr ?? '').toLowerCase().includes(q) ||
					(o.fn ?? '').toLowerCase().includes(q) ||
					(o.ln ?? '').toLowerCase().includes(q) ||
					(o.eml ?? '').toLowerCase().includes(q)
				}
				defaultSort={{ key: 'ln', dir: 'asc' }}
				emptyIcon={<BriefcaseBusiness size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('customers.salesPersons.add')}
				onAdd={() => {
					setEditRow(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditRow(o);
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				rowActions={o => (
					<>
						<Button
							variant="ghost"
							size="icon"
							aria-label={t('customers.links.inspectors')}
							title={t('customers.links.inspectors')}
							onClick={() => setInspectorsFor(o)}>
							<ClipboardCheck size={15} />
						</Button>
						<Button
							variant="ghost"
							size="icon"
							aria-label={t('customers.links.customers')}
							title={t('customers.links.customers')}
							onClick={() => setCustomersFor(o)}>
							<Users size={15} />
						</Button>
					</>
				)}
				exportFilename="sales-persons"
			/>

			<SalesPersonEditDialog open={editOpen} onOpenChange={setEditOpen} row={editRow} all={all} />

			<LinkToggleDialog
				open={Boolean(inspectorsFor)}
				onOpenChange={open => !open && setInspectorsFor(null)}
				title={t('customers.links.inspectorsTitle', { name: inspectorsFor ? fullName(inspectorsFor) : '' })}
				rows={linkedInspectors.data ?? []}
				loading={linkedInspectors.isLoading}
				getKey={row => row.id ?? row.iid}
				isLinked={row => Boolean(row.iil)}
				onToggle={(row, next) => toggleInspector.mutateAsync({ row, linked: next })}
				toggling={toggleInspector.isPending}
				columns={[
					{ header: t('customers.inspectors.fields.name'), value: row => row.inspector?.fn ?? '' },
					{ header: t('customers.inspectors.fields.surname'), value: row => row.inspector?.ln ?? '' },
					{ header: t('customers.inspectors.fields.tel'), value: row => row.inspector?.tel ?? '' },
					{ header: t('customers.inspectors.fields.mobile'), value: row => row.inspector?.mob ?? '' }
				]}
				searchText={row => `${row.inspector?.fn ?? ''} ${row.inspector?.ln ?? ''}`}
				emptyText={t('common.noResults')}
			/>

			<LinkToggleDialog
				open={Boolean(customersFor)}
				onOpenChange={open => !open && setCustomersFor(null)}
				title={t('customers.links.customersTitle', { name: customersFor ? fullName(customersFor) : '' })}
				rows={linkedCustomers.data ?? []}
				loading={linkedCustomers.isLoading}
				getKey={row => row.id ?? row.cid}
				isLinked={row => Boolean(row.icl)}
				onToggle={(row, next) => toggleCustomer.mutateAsync({ row, linked: next })}
				toggling={toggleCustomer.isPending}
				columns={[
					{ header: t('customers.list.columns.email'), value: row => row.customer?.eml ?? '' },
					{ header: t('customers.list.columns.name'), value: row => row.customer?.fn ?? '' },
					{ header: t('customers.list.columns.surname'), value: row => row.customer?.ln ?? '' }
				]}
				searchText={row => `${row.customer?.eml ?? ''} ${row.customer?.fn ?? ''} ${row.customer?.ln ?? ''}`}
				emptyText={t('common.noResults')}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('customers.salesPersons.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('customers.salesPersons.deleteBody', { name: deleteRow ? fullName(deleteRow) : '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
